// Map-panel toolbar: one grouped row — an optional Filter group (bbox "in view" + "draw area"
// buttons, supplied by the Table Explorer host via `filterControls`; the Query Editor Map view
// passes none), the geometry-column picker (only when the result has >1 spatial column) and the
// color-by picker, then the feature count pinned right. Consolidating these here fixes the old
// overlap where the host floated the filter buttons ON the map, over these selectors. Picker state
// is component-local in the panel (NOT persisted); analytics fire via the onChange callbacks.

import type { ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';

const COLOR_BY_NONE = '__none__';

type ColumnOption = { id: string; label: string };

export function SpatialResultsMapControls({
  geometryOptions,
  geometryColumnId,
  onGeometryChange,
  colorByOptions,
  colorByColumnId,
  onColorByChange,
  filterControls,
  featureCount,
}: {
  geometryOptions: ColumnOption[];
  geometryColumnId: string;
  onGeometryChange: (columnId: string) => void;
  colorByOptions: ColumnOption[];
  colorByColumnId: string | null;
  onColorByChange: (columnId: string | null) => void;
  /** TE-only Filter group (bbox + draw-area buttons). Undefined on the QE Map view. */
  filterControls?: ReactNode;
  featureCount: number;
}) {
  const hasFieldGroup = geometryOptions.length > 1 || colorByOptions.length > 0;
  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 px-2 py-1.5 text-xs">
      {filterControls && (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span className="text-muted-foreground">Filter</span>
          <div className="flex flex-wrap items-center gap-1">{filterControls}</div>
        </div>
      )}

      {filterControls && hasFieldGroup && <span className="h-4 w-px bg-border" aria-hidden />}

      {geometryOptions.length > 1 && (
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Geometry</span>
          <Select value={geometryColumnId} onValueChange={onGeometryChange}>
            <SelectTrigger className="h-6 w-36 text-xs" data-testid="spatial-map-geometry-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {geometryOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}

      {colorByOptions.length > 0 && (
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Color by</span>
          <Select
            value={colorByColumnId ?? COLOR_BY_NONE}
            onValueChange={(v) => onColorByChange(v === COLOR_BY_NONE ? null : v)}
          >
            <SelectTrigger className="h-6 w-36 text-xs" data-testid="spatial-map-colorby-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={COLOR_BY_NONE} className="text-xs">
                None
              </SelectItem>
              {colorByOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}

      <span
        className="ml-auto whitespace-nowrap text-muted-foreground"
        data-testid="spatial-map-feature-count"
      >
        {featureCount.toLocaleString()} {featureCount === 1 ? 'feature' : 'features'}
      </span>
    </div>
  );
}
